import apiClient from './api';

export interface PlanFeatures {
  buildings: number; // -1 = unlimited
  units: number; // -1 = unlimited
  publicListings: number; // -1 = unlimited
  apiRequestsPerMin: number;
  dailyCap: number; // -1 = unlimited
  corsDomains: number; // -1 = unlimited
  webhooks: boolean;
  verifiedBadge: boolean;
  blogCms: boolean;
  customDomain: boolean;
  mapSearch: boolean;
  listingAnalytics: boolean;
  hostedSubdomain: boolean;
  mortgageCalculator: boolean;
}

export interface PlanInfo {
  tier: string;
  name: string;
  tagline: string;
  pricing: {
    monthly: number | null;
    annual: number | null;
    originalMonthly?: number;
    originalAnnual?: number;
  };
  paygoOnly: boolean;
  /** Quoted individually: no list price, no self-serve checkout. */
  contactOnly?: boolean;
  features: PlanFeatures;
}

export interface ActiveCampaign {
  name: string;
  discountPercent: number;
  endsAt: string;
}

export interface PlansResponse {
  campaign: ActiveCampaign | null;
  plans: PlanInfo[];
}

export interface SubscribePayload {
  planTier: string;
  billingCycle: 'MONTHLY' | 'ANNUAL';
  /** Custom duration in months, multiplied against monthly rate (1–24). */
  durationMonths?: number;
  callbackUrl: string;
}

export interface SubscribeResponse {
  paymentId: number;
  paystackRef: string;
  authorizationUrl: string;
  accessCode: string;
}

export const plansService = {
  async getPlans(): Promise<PlansResponse> {
    const res = await apiClient.get('/payments/plans');
    return res.data;
  },
  async subscribe(payload: SubscribePayload): Promise<SubscribeResponse> {
    const res = await apiClient.post('/payments/subscribe', payload);
    return res.data;
  },
  async verifyPayment(ref: string): Promise<{ status: string }> {
    const res = await apiClient.get(`/payments/verify/${ref}`);
    return res.data;
  },
};

export interface SubscriptionStatus {
  plan: string;
  planExpiresAt: string | null;
  /** ACTIVE | GRACE (lapsed, can't add buildings/units) | READ_ONLY (grace used up). */
  phase: 'ACTIVE' | 'GRACE' | 'READ_ONLY';
  graceDaysLeft: number;
  graceDaysTotal: number;
  limits: { buildings: number; units: number };
  usage: { buildings: number; units: number };
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  const { data } = await apiClient.get<SubscriptionStatus | null>('/payments/subscription');
  return data;
}
